import { useEffect, useRef, useState } from "react";
import { BrandName } from "@/lib/brand";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck, ShieldCheck } from "lucide-react";
import { openInvitation, startInvitation, claimInvitation } from "@/lib/inviteApi";
import { EMAIL_RE } from "@/lib/applyApi";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const err = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-start sm:items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-800"><BrandName /></div>
        {children}
      </div>
    </div>
  );
}

function StartForm({ inv, token, onSent }) {
  const [name, setName] = useState(inv.seller_name || "");
  const [email, setEmail] = useState(inv.seller_email || "");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const send = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return setProblem("Please enter your full name.");
    if (!EMAIL_RE.test(email.trim())) return setProblem("Please enter a valid email address.");
    setBusy(true); setProblem("");
    try { await startInvitation(token, { full_name: name.trim(), email: email.trim() }); onSent(email.trim()); }
    catch (x) { setProblem(err(x, "Could not send the verification email.")); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="mt-5 space-y-4" noValidate>
      <div className="space-y-1"><Label htmlFor="inv-name">Full Name</Label>
        <Input id="inv-name" data-testid="invite-landing-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} className="h-12 text-base" /></div>
      <div className="space-y-1"><Label htmlFor="inv-email">Email Address</Label>
        <Input id="inv-email" data-testid="invite-landing-email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 text-base" /></div>
      {problem && <p data-testid="invite-landing-error" role="alert" className="text-sm text-red-700">{problem}</p>}
      <Button type="submit" data-testid="invite-landing-submit" disabled={busy} className="w-full h-12 rounded-full bg-amber-800 hover:bg-amber-900 text-white text-base">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify My Email"}
      </Button>
      <p className="text-xs text-stone-500 flex items-start gap-1.5"><ShieldCheck className="h-4 w-4 shrink-0 text-emerald-700" />We'll email you a secure sign-in link. After verifying, you'll go straight to your Horse Sale Advertising Agreement.</p>
    </form>
  );
}

export default function InviteLanding() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [fatal, setFatal] = useState("");
  const [sent, setSent] = useState("");
  const claimed = useRef(false);

  useEffect(() => { openInvitation(token).then(setInv).catch((e) => setFatal(err(e, "This invitation link isn't valid."))); }, [token]);

  const claim = async () => {
    if (claimed.current) return;
    claimed.current = true;
    try { const r = await claimInvitation(token); navigate(`/dashboard/applications/${r.application_id}`, { replace: true }); }
    catch (e) { claimed.current = false; setFatal(err(e, "This invitation can no longer be used.")); }
  };
  useEffect(() => {
    if (inv && user && params.get("verified") === "1") { claim(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv, user]);

  if (fatal) return <Shell><h1 className="mt-2 font-display text-2xl font-extrabold text-stone-900">Invitation unavailable</h1><p data-testid="invite-landing-fatal" className="mt-3 text-sm text-stone-700">{fatal}</p></Shell>;
  if (!inv || user === null || (user && params.get("verified") === "1")) return <Shell><Loader2 data-testid="invite-landing-loading" className="mt-6 h-6 w-6 animate-spin text-amber-800 mx-auto" /></Shell>;
  if (sent) return (
    <Shell>
      <MailCheck className="mt-4 h-10 w-10 text-emerald-700" />
      <h1 className="mt-2 font-display text-2xl font-extrabold text-stone-900">Check your email</h1>
      <p data-testid="invite-landing-sent" className="mt-3 text-sm text-stone-700">We sent a sign-in link to <strong>{sent}</strong>. Open it on this device within 15 minutes to continue to your agreement.</p>
      <Button variant="outline" data-testid="invite-landing-resend" onClick={() => setSent("")} className="mt-5 h-11 rounded-full">Use a different email or resend</Button>
    </Shell>
  );
  return (
    <Shell>
      <h1 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-stone-900">You're pre-approved{inv.horse_name ? ` to advertise ${inv.horse_name}` : ""}</h1>
      <p className="mt-2 text-sm text-stone-600">{inv.claimed ? "This invitation has already been started. Enter the same email to get a new sign-in link." : "No application needed. Enter your name and email to get started."}</p>
      {user && !inv.claimed && (
        <div data-testid="invite-landing-signed-in" className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm text-stone-700">
          Signed in as <strong>{user.email}</strong>.
          <Button data-testid="invite-landing-continue" onClick={claim} className="mt-2 w-full h-11 rounded-full bg-primary hover:bg-amber-900 text-white">Continue as {user.email}</Button>
        </div>
      )}
      <StartForm inv={inv} token={token} onSent={setSent} />
    </Shell>
  );
}
